import { useContext, useEffect, useState } from 'react';
import useFetch from '~/hooks/useFetch';
import { FuelContext } from '~/providers/FuelProvider';
import { UseFormRegister, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { formatPhone, unformatPhone } from '~/helpers/phone';
import { Contact, ContactErrors, ContactResponse } from '~/types/contact-credit';
import { setContact } from '~/redux/actions/contact-credit';
import { setNewRelicPageAction, setTrackingCustomAttributes } from '~/helpers/newrelic';
import { convertSuffixToUpperCase } from '~/helpers/contact';
import { DEFAULT_API_VERSION } from '~/constants/config/common';
import router from 'next/router';
import { NoArgVoid } from '~/types/common';

export interface OutageContactReturnType {
  apiVersion: string;
  contactSubmitted: boolean;
  contactFormProps: {
    register: UseFormRegister<Contact>;
    handleSubmit: NoArgVoid;
    setValue: (name: string, value: string) => void;
    errors: ContactErrors;
    formValid: boolean;
    loading: boolean;
  };
}

const useOutageContactForm = (): OutageContactReturnType => {
  const dispatch = useDispatch();
  const { trackEvent } = useContext(FuelContext);
  const { contact, tracking, monarch } = useSelector((store: ReduxState) => store);

  const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
  const [contactSubmitted, setContactSubmitted] = useState<boolean>(false);

  setTrackingCustomAttributes(tracking);

  // contact
  const {
    data: contactData,
    loading: contactLoading,
    fetch: submitContact,
  } = useFetch<ContactResponse>(`${apiVersion}/contact`, { method: 'post' });

  const contactForm = useForm<Contact>({
    mode: 'onChange',
    defaultValues: {
      firstname: contact?.firstname.trim(),
      lastname: contact?.lastname.trim(),
      email: contact?.email,
      phone: formatPhone(unformatPhone(contact?.phone || '')),
      tcpaMarketing: contact?.tcpaMarketing === undefined ? false : contact.tcpaMarketing,
      tcpaService: contact?.tcpaService === undefined ? false : contact.tcpaService,
    },
  });

  // Contact submissions
  useEffect(() => {
    if (contactData?.success) {
      dispatch(
        setContact({
          ...contactForm.getValues(),
          gmtOffset: contact?.gmtOffset,
        })
      );
      setContactSubmitted(true);
    }
  }, [contactData]);

  interface ContactFormData {
    phone: string;
    tcpaMarketing: boolean;
    tcpaService: boolean;
    firstname: string;
    lastname: string;
    email: string;
  }

  const handleContactSubmit = contactForm.handleSubmit((data: ContactFormData) => {
    const { phone, tcpaMarketing, tcpaService, firstname, lastname, email } = data;
    trackEvent({
      action: 'personalDetailsEntered',
      data: {
        formType: 'Cart Outage Contact',
        formName: 'Frontier cart email',
        formId: '',
      },
    });
    setNewRelicPageAction('outageContactSubmitClicked');

    submitContact({
      body: JSON.stringify({
        phone: phone,
        tcpaMarketing: tcpaMarketing,
        tcpaService: tcpaService,
        firstName: firstname?.trim(),
        lastName: lastname ? convertSuffixToUpperCase(lastname.trim()) : '',
        email: email,
      }),
    });

    router.push('/outage-contact-confirmation');
  });

  return {
    apiVersion,
    contactSubmitted,
    contactFormProps: {
      register: contactForm.register,
      handleSubmit: handleContactSubmit,
      setValue: contactForm.setValue,
      errors: contactForm.formState.errors,
      formValid: contactForm.formState.isValid,
      loading: contactLoading,
    },
  };
};

export default useOutageContactForm;
